import type {
	MapSearchOption,
	MapSearchOptionType
} from '$mapapp/MapApp/SlideIn/SearchRoutingHistory/MapSearch/MapSearch.Model'
import type { MapMarker } from '$mapapp/MapApp/MapView/Marker/BasicMarker.model'
import { SvelteSet } from 'svelte/reactivity'

type RoutingContext = {
	isRoutePlanerOpened: () => boolean
	sendRouting: () => void
}

// This wrapper contains a stable id, which is needed for drag-and-drop handling in the route planner.
export type RouteStop = { id: string; stop: MapSearchOption | null }

export type Coordinates = { lat: number; lon: number }

export class RouteState {
	// computed polyline; map layers read `.coordinates`
	coordinates: [number, number][] | null = $state(null)
	// start handoff: set by the fsm, consumed by the routeplanner on mount
	source: MapSearchOption | null = $state(null)
	// ordered stops; each carries a stable id so drag reordering + keyed {#each} stay correct
	stops: RouteStop[] = $state([])

	#idCounter = 0
	// Notified when the stop list changes in a way that warrants recomputing the route.
	// An explicit observer (not a $effect) so the routeplanner recomputes exactly once
	// per committing mutation — including changes from another component — without
	// firing on every drag `consider` tick. Never read in a reactive context, but uses
	// SvelteSet to satisfy svelte/prefer-svelte-reactivity (matches the SvelteMap usage
	// elsewhere in this store layer).
	readonly #listeners = new SvelteSet<() => void>()
	#context: RoutingContext | null = null

	setCoordinates(coordinates: [number, number][]) {
		this.coordinates = coordinates
	}

	clearCoordinates() {
		this.coordinates = null
	}

	// Subscribe to stop changes (returns an unsubscribe).
	onStopsChange(listener: () => void): () => void {
		this.#listeners.add(listener)
		return () => this.#listeners.delete(listener)
	}

	#notifyStopsChange() {
		this.#listeners.forEach((listener) => listener())
	}

	#createStop(stop: MapSearchOption | null = null): RouteStop {
		return { id: `route-stop-${this.#idCounter++}`, stop }
	}

	// Accepts a map marker (converted to a search option) or a search option.
	#toStopOption(stop: MapSearchOption | MapMarker | null): MapSearchOption | null {
		if (stop === null) return null
		if ('markerId' in stop && 'lat' in stop && 'lon' in stop) {
			return {
				label: stop.objectLabel ?? 'Unbekannter Ort',
				id: stop.markerId,
				coordinates: { lat: stop.lat, lon: stop.lon },
				type: 'place'
			} as MapSearchOption
		}
		return stop
	}

	addStop(stop: MapSearchOption | MapMarker | null = null) {
		const option = this.#toStopOption(stop)
		if (this.stops.length === 2 && this.stops.some((stop) => !stop.stop)) {
			// replace empty stop if we only have source/dest
			const emptyStopIdx = this.stops.findIndex((stop) => !stop.stop)
			this.stops[emptyStopIdx] = this.#createStop(option)
		} else {
			this.stops.push(this.#createStop(option))
		}
		// Only a stop with coordinates can change the computed route; an empty stop
		// (the "add stop" button) must not trigger a recompute.
		if (option?.coordinates) this.#notifyStopsChange()
	}

	// Keeps the stop's stable id; only its selected option changes.
	changeStop(stop: MapSearchOption | null, index: number) {
		this.stops[index].stop = stop
		this.#notifyStopsChange()
	}

	removeStop(indexToRemove: number) {
		this.stops.splice(indexToRemove, 1)
		this.#notifyStopsChange()
	}

	reverseStops() {
		this.stops.reverse()
		this.#notifyStopsChange()
	}

	// Low-level setter used by drag-and-drop on every `consider`/`finalize`; must NOT notify.
	setStops(stops: RouteStop[]) {
		this.stops = stops
	}

	clearStops() {
		this.stops = []
	}

	/** inject routing context so RouteState can act on FSM/planner state without importing appState */
	init(context: RoutingContext): void {
		this.#context = context
	}

	// build an ad-hoc place option for a bare map coordinate, labelled with the coords
	toPlaceOption({ lat, lon }: { lat: number; lon: number }): MapSearchOption {
		// Hint: toFixed(5) = 5 decimal places has a granularity of min 1,11m, which should be fine for a label anyway.
		return {
			label: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
			id: `stop-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
			coordinates: { lat, lon },
			type: 'place'
		} as MapSearchOption
	}

	/**
	 * set the clicked point as the route's start ('Route von hier').
	 *
	 * planner open: overwrite the first stop field. planner closed: pre-populate
	 * the stops before sending set_routing — Routeplanner.onMount only seeds
	 * start/destination when `appState.route.stops` is empty, and adds the empty
	 * destination field when exactly one stop exists.
	 */
	routeFromHere(payload: { lat: number; lon: number } | null, close: () => void) {
		if (!payload) return

		const start = this.toPlaceOption(payload)
		if (this.#context?.isRoutePlanerOpened()) {
			this.changeStop(start, 0)
		} else {
			this.addStop(start)
			this.#context?.sendRouting()
		}
		close()
	}

	routeFromMarker(markerInfo: MapMarker | null, close?: () => void) {
		if (!markerInfo) return

		const option: MapSearchOption = {
			id: markerInfo.markerId,
			label: markerInfo.objectLabel ?? `${markerInfo.lat}, ${markerInfo.lon}`,
			type: markerInfo.type as MapSearchOptionType
		}

		if (this.stops.length === 0) {
			this.addStop(option)
		} else {
			this.changeStop(option, 0)
		}

		if (!this.#context?.isRoutePlanerOpened()) {
			this.#context?.sendRouting()
		}
		close?.()
	}

	/**
	 * set the clicked point as the route's destination ('Route nach hier').
	 *
	 * planner open: overwrite the last stop field. planner closed: pre-populate an empty start plus the destination
	 */
	routeToHere(payload: { lat: number; lon: number } | null, close?: () => void) {
		if (!payload) return

		const destination = this.toPlaceOption(payload)
		if (this.#context?.isRoutePlanerOpened()) {
			this.changeStop(destination, this.stops.length - 1)
		} else {
			this.addStop(null)
			this.addStop(destination)
			this.#context?.sendRouting()
		}
		close?.()
	}

	routeToMarker(markerInfo: MapMarker | null, close?: () => void) {
		if (!markerInfo) return

		const option: MapSearchOption = {
			id: markerInfo.markerId,
			label: markerInfo.objectLabel ?? `${markerInfo.lat}, ${markerInfo.lon}`,
			type: markerInfo.type as MapSearchOptionType
		}

		if (this.stops.length === 0) {
			this.addStop(null)
			this.addStop(option)
		} else {
			this.changeStop(option, this.stops.length - 1)
		}

		if (!this.#context?.isRoutePlanerOpened()) {
			this.#context?.sendRouting()
		}
		close?.()
	}
}
